/**
 * hz-for 循环指令处理器
 */

import type {
  ForElementState,
  GetDataFn,
  DataValue,
  DataArray,
} from "../types";

/**
 * hz-for 循环指令处理器
 * 语法: hz-for="item in this.items" 或 hz-for="(item, index) in this.items"
 */
export class ForDirective {
  /** 存储每个 hz-for 模板元素的状态 */
  private forState = new Map<Element, ForElementState>();

  /**
   * 处理 hz-for 指令
   * @param root 根元素
   * @param getData 获取数据的函数
   * @param processNode 处理生成节点的回调（用于处理 hz-if, hz-click 等）
   */
  process(
    root: Element,
    getData: GetDataFn,
    processNode: (el: Element, itemData: Record<string, DataValue>) => void,
  ): void {
    if (!(root instanceof Element)) {
      return;
    }

    // 1. 处理已存在的 hz-for 元素（首次发现时记录并替换为占位符）
    const children = Array.from(root.children) as Element[];
    for (const child of children) {
      const hzForExpr = child.getAttribute("hz-for");
      if (hzForExpr !== null) {
        // 解析表达式
        const parsed = this.parseExpression(hzForExpr);
        if (!parsed) {
          console.warn("hz-for 解析失败:", hzForExpr);
          continue;
        }

        // 如果是第一次遇到，保存模板并替换为占位符
        if (!this.forState.has(child)) {
          const placeholder = document.createComment("hz-for placeholder");
          const parent = child.parentNode!;
          parent.replaceChild(placeholder, child);

          this.forState.set(child, {
            template: child,
            placeholder,
            parent,
            expression: parsed.expression,
            itemVariable: parsed.itemVariable,
            indexVariable: parsed.indexVariable,
            generatedElements: [],
          });
        }
      } else {
        // 递归处理子元素
        this.process(child, getData, processNode);
      }
    }

    // 2. 更新所有已注册的 hz-for
    for (const [, state] of Array.from(this.forState.entries())) {
      this.updateForLoop(state, getData, processNode);
    }
  }

  /**
   * 解析 hz-for 表达式
   * 支持: "item in this.items" 或 "(item, index) in this.items"
   */
  private parseExpression(expr: string): {
    itemVariable: string;
    indexVariable?: string;
    expression: string;
  } | null {
    // 匹配 "(item, index) in this.xxx" 或 "item in this.xxx"
    const matchWithIndex = expr.match(
      /^\s*\(\s*(\w+)\s*,\s*(\w+)\s*\)\s+in\s+this\.(\S+)\s*$/,
    );
    if (matchWithIndex) {
      return {
        itemVariable: matchWithIndex[1],
        indexVariable: matchWithIndex[2],
        expression: matchWithIndex[3],
      };
    }

    const matchSimple = expr.match(/^\s*(\w+)\s+in\s+this\.(\S+)\s*$/);
    if (matchSimple) {
      return {
        itemVariable: matchSimple[1],
        expression: matchSimple[2],
      };
    }

    return null;
  }

  /**
   * 更新循环渲染
   */
  private updateForLoop(
    state: ForElementState,
    getData: GetDataFn,
    processNode: (el: Element, itemData: Record<string, DataValue>) => void,
  ): void {
    const {
      template,
      placeholder,
      parent,
      expression,
      itemVariable,
      indexVariable,
      generatedElements,
    } = state;

    // 获取数据数组
    const dataArray = getData(expression);
    const items: DataValue[] = Array.isArray(dataArray)
      ? (dataArray as DataArray)
      : [];

    // 清除旧的生成元素
    for (const el of generatedElements) {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    }
    generatedElements.length = 0;

    // 找到插入位置（placeholder 之前）
    const insertBefore: Node | null = placeholder;

    // 为每个数据项生成元素
    for (let index = 0; index < items.length; index++) {
      const item = items[index];
      const clonedElement = template.cloneNode(true) as Element;

      // 移除 hz-for 属性，避免重复处理
      clonedElement.removeAttribute("hz-for");

      // 创建局部数据上下文
      const itemData: Record<string, DataValue> = {
        [itemVariable]: item,
      };

      if (indexVariable != null) {
        itemData[indexVariable] = index;
      }

      // 替换模板中的 item.xxx 引用
      this.replaceItemReferences(
        clonedElement,
        itemVariable,
        item,
        indexVariable,
        index,
      );

      // 插入到 DOM
      parent.insertBefore(clonedElement, insertBefore);
      generatedElements.push(clonedElement);

      // 调用 processNode 处理子指令（hz-if, hz-click 等）
      processNode(clonedElement, itemData);
    }
  }

  /**
   * 替换元素中的 item 变量引用
   */
  private replaceItemReferences(
    element: Element,
    itemVariable: string,
    itemValue: DataValue,
    indexVariable?: string,
    indexValue?: number,
  ): void {
    // 处理属性
    const attributes = Array.from(element.attributes);
    for (const attr of attributes) {
      if (
        attr.value.includes(`{${itemVariable}`) ||
        attr.value.includes(`{{`)
      ) {
        const newValue = this.replaceItemTemplate(
          attr.value,
          itemVariable,
          itemValue,
          indexVariable,
          indexValue,
        );
        if (newValue !== attr.value) {
          element.setAttribute(attr.name, newValue);
        }
      }
    }

    // 递归处理子节点
    const processChildren = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (
          text !== null &&
          text !== "" &&
          (text.includes(`{${itemVariable}`) || text.includes(`{{`))
        ) {
          const newText = this.replaceItemTemplate(
            text,
            itemVariable,
            itemValue,
            indexVariable,
            indexValue,
          );
          if (newText !== text) {
            node.textContent = newText;
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        // 处理元素属性
        const attrs = Array.from(el.attributes);
        for (const attr of attrs) {
          if (
            attr.value.includes(`{${itemVariable}`) ||
            attr.value.includes(`{{`)
          ) {
            const newValue = this.replaceItemTemplate(
              attr.value,
              itemVariable,
              itemValue,
              indexVariable,
              indexValue,
            );
            if (newValue !== attr.value) {
              el.setAttribute(attr.name, newValue);
            }
          }
        }
        // 递归处理子节点
        for (const child of Array.from(node.childNodes)) {
          processChildren(child);
        }
      }
    };

    for (const child of Array.from(element.childNodes)) {
      processChildren(child);
    }
  }

  /**
   * 替换模板字符串中的 item 引用
   */
  private replaceItemTemplate(
    text: string,
    itemVariable: string,
    itemValue: DataValue,
    indexVariable?: string,
    indexValue?: number,
  ): string {
    let result = text;

    // 替换 {item.xxx} 或 {item}
    result = result.replace(
      new RegExp(`\\{${itemVariable}(\\.([^}]+))?\\}`, "g"),
      (_match: string, _dotPart: string, subKey: string): string => {
        if (subKey) {
          const value = this.getNestedValue(itemValue, subKey);
          return value !== undefined ? String(value) : _match;
        }
        return itemValue !== undefined ? String(itemValue) : _match;
      },
    );

    // 替换 {{item.xxx}} 或 {{item}}
    result = result.replace(
      new RegExp(`\\{\\{\\s*${itemVariable}(\\.([^}]+))?\\s*}}`, "g"),
      (_match: string, _dotPart: string, subKey: string): string => {
        if (subKey) {
          const value = this.getNestedValue(itemValue, subKey);
          return value !== undefined ? String(value) : _match;
        }
        return itemValue !== undefined ? String(itemValue) : _match;
      },
    );

    // 替换 index 变量
    if (indexVariable !== undefined && indexValue !== undefined) {
      result = result.replace(
        new RegExp(`\\{${indexVariable}\\}`, "g"),
        String(indexValue),
      );
      result = result.replace(
        new RegExp(`\\{\\{\\s*${indexVariable}\\s*}}`, "g"),
        String(indexValue),
      );
    }

    return result;
  }

  /**
   * 从嵌套对象中获取值
   */
  private getNestedValue(data: DataValue, key: string): DataValue | undefined {
    if (data === null || data === undefined) {
      return undefined;
    }

    const keys = key.split(".");
    let value: DataValue = data;

    for (const k of keys) {
      if (
        value != undefined &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        !(value instanceof Event) &&
        k in value
      ) {
        value = (value as Record<string, DataValue>)[k];
      } else {
        return undefined;
      }
    }

    return value;
  }

  /**
   * 检查元素是否是 hz-for 生成的
   */
  isGenerated(element: Element): boolean {
    for (const state of this.forState.values()) {
      if (state.generatedElements.includes(element)) {
        return true;
      }
    }
    return false;
  }

  /**
   * 清空所有状态
   */
  clear(): void {
    // 清除所有生成的元素
    for (const state of this.forState.values()) {
      for (const el of state.generatedElements) {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      }
    }
    this.forState.clear();
  }
}
